import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import { SimpleSelect } from 'react-selectize-advizr';
import AdvForm from '../../../CommonComponents/AdvForm';
import Footer from '../../Footer/FooterContainer';
import PlanImpact from '../../PlanImpact/PlanImpactContainer';
import Loading from '../../Loading/LoadingView';
import {
  simpleSelectDefaultValue,
  inputCheckboxClicked,
  simpleSelectChangedToNonNull,
} from '../../../Profile/Accounts_react/AccountsUtils';
import { isNumeric, isUuid, createCustomPropType } from '../../../../js/app/react/validators';
import ValidationErrors from '../../ValidationErrors/ValidationErrorsContainer';
import { MAXIMIZE_LIFETIME_BENEFITS, MAXIMIZE_NET_WORTH } from './MonthlyBenefitsConstants';
import AdvInput, { advInputTextChanged } from '../../../CommonComponents/AdvInput';
import AdvInputWrapper from '../../../CommonComponents/AdvInputWrapper';
import Tooltip from '../../../CommonComponents/Tooltip';

const FREQUENCY_OPTIONS = [
  { value: 0, label: 'Yearly' },
  { value: 1, label: 'Monthly' },
];

class MonthlyBenefitsView extends Component {
  componentDidMount() {
    const {
      fetchData,
      clientId,
      fpId,
      goalId,
      fetchInitialPlanImpact,
      whatIf,
      whatIfTemplate,
      spouseIfFRA,
      spouseSelectedAge,
      principalIsFRA,
      principalSelectedAge,
    } = this.props;

    this.setState({
      initialPrincipalSelectedAge: principalIsFRA ? 'FRA' : principalSelectedAge,
      initialSpouseSelectedAge: spouseIfFRA ? 'FRA' : spouseSelectedAge,
    });

    fetchData(clientId, fpId, goalId, whatIfTemplate.WhatIfType);
    fetchInitialPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
  }

  componentDidUpdate(previousProps) {
    const { clientId, fpId, goalId, whatIf, fetchPlanImpact, whatIfTemplate } = this.props;

    const impactTriggerProps = ['whatIf', 'fpId', 'isOPLMonteCarlo'];

    if (!isEqual(pick(this.props, impactTriggerProps), pick(previousProps, impactTriggerProps))) {
      fetchPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
    }
  }

  render() {
    const {
      goalId,
      clientId,
      loading,
      planImpactItems,
      groupTitle,
      fpId,
      whatIf,
      whatIfTemplate,
      applyWhatIf,
      areOptionsLoaded,
      optimizeSocialSecurity,
      storeData,
      principalName,
      partnerName,
      hasPartner,
      principalSelectedAge,
      spouseSelectedAge,
      fileForSurvivorBenefits,
      useSpousalBenefit,
      amountSpouse,
      amountPrincipal,
      socialSecurityCurrentlyReceivingBenefitsPrincipal,
      socialSecurityCurrentlyReceivingBenefitsPartner,
      socialSecurityIncomeFrequencyPrincipal,
      socialSecurityIncomeFrequencySpouse,
      principalIsCurrentlyReceivingBenefitsUnderCurrentPlan,
      spouseIsCurrentlyReceivingBenefitsUnderCurrentPlan,
      principalAgeOptions,
      spouseAgeOptions,
    } = this.props;

    const initialPrincipalSelectedAge =
      (this.state && this.state.initialPrincipalSelectedAge) || null;

    const initialSpouseSelectedAge = (this.state && this.state.initialSpouseSelectedAge) || null;

    const { ADV } = window;

    return (
      <div className="what-ifs-layout">
        {loading && <Loading />}
        <div className="what-ifs-section what-ifs-layout-header">
          <h3>What-If...</h3>
        </div>

        <div className="what-ifs-layout-content">
          <h4 className="what-if-group-title">{groupTitle}</h4>

          <p className="what-if-social-security-title-description">
            Monthly social security benefits are derived from the Primary Insurance Amount and start
            age.
          </p>

          {(!principalIsCurrentlyReceivingBenefitsUnderCurrentPlan ||
            (hasPartner && !spouseIsCurrentlyReceivingBenefitsUnderCurrentPlan)) && (
            <div className="what-if-social-security-layout-buttons">
              <div className="what-if-social-security-layout-maximize">
                <Tooltip
                  tag="button"
                  className="what-ifs-social-security-button-maximize"
                  onClick={optimizeSocialSecurity(
                    MAXIMIZE_NET_WORTH,
                    clientId,
                    fpId,
                    goalId,
                    whatIfTemplate.WhatIfType,
                  )}
                  tooltipchildren="Maximizes net worth at the end of the plan"
                  orientation="left"
                >
                  Maximize Assets
                </Tooltip>
              </div>
              <div className="what-if-social-security-layout-maximize">
                <Tooltip
                  tag="button"
                  className="what-ifs-social-security-button-maximize"
                  onClick={optimizeSocialSecurity(
                    MAXIMIZE_LIFETIME_BENEFITS,
                    clientId,
                    fpId,
                    goalId,
                    whatIfTemplate.WhatIfType,
                  )}
                  tooltipchildren="Maximizes the total lifetime benefits received"
                  orientation="left"
                >
                  Maximize Benefits
                </Tooltip>
              </div>
            </div>
          )}

          <AdvForm>
            <ValidationErrors clientId={clientId} />

            <ul className={`what-ifs-form-fields what-ifs-box${areOptionsLoaded ? ' loaded' : ''}`}>
              <li className="what-ifs-form-field">
                <h5 className="what-if-title">Who is Currently Receiving Benefits?</h5>
              </li>
              <li className="what-ifs-form-field what-ifs-form-field-no-box">
                <label
                  htmlFor="SocialSecurityCurrentlyReceivingBenefitsPrincipal"
                  className="what-ifs-age-label what-ifs-checkbox"
                >
                  <input
                    type="checkbox"
                    id="SocialSecurityCurrentlyReceivingBenefitsPrincipal"
                    className="what-ifs-checkbox-toggle"
                    checked={socialSecurityCurrentlyReceivingBenefitsPrincipal}
                    disabled={principalIsCurrentlyReceivingBenefitsUnderCurrentPlan}
                    onChange={inputCheckboxClicked(
                      clientId,
                      storeData(goalId, whatIfTemplate.WhatIfType),
                      'WhatIf.SocialSecurityCurrentlyReceivingBenefitsPrincipal',
                    )}
                  />
                  <i className="what-ifs-checkbox-mark" />
                  <span className="what-ifs-checkbox-label-text">{principalName}</span>
                </label>
              </li>

              {hasPartner && (
                <li className="what-ifs-form-field what-ifs-form-field-no-box">
                  <label
                    htmlFor="SocialSecurityCurrentlyReceivingBenefitsSpouse"
                    className="what-ifs-age-label what-ifs-checkbox"
                  >
                    <input
                      type="checkbox"
                      id="SocialSecurityCurrentlyReceivingBenefitsSpouse"
                      className="what-ifs-checkbox-toggle"
                      checked={socialSecurityCurrentlyReceivingBenefitsPartner}
                      disabled={spouseIsCurrentlyReceivingBenefitsUnderCurrentPlan}
                      onChange={inputCheckboxClicked(
                        clientId,
                        storeData(goalId, whatIfTemplate.WhatIfType),
                        'WhatIf.SocialSecurityCurrentlyReceivingBenefitsSpouse',
                      )}
                    />
                    <i className="what-ifs-checkbox-mark" />
                    <span className="what-ifs-checkbox-label-text">{partnerName}</span>
                  </label>
                </li>
              )}
            </ul>

            {/* PRIMARY */}
            {!principalIsCurrentlyReceivingBenefitsUnderCurrentPlan && (
              <ul
                className={`what-ifs-form-fields what-ifs-box${areOptionsLoaded ? ' loaded' : ''}`}
              >
                <li className="what-ifs-form-field">
                  <h5 className="what-if-title">
                    {principalName}{' '}
                    {socialSecurityCurrentlyReceivingBenefitsPrincipal ? (
                      'Current Benefit Amount'
                    ) : (
                      <>
                        Change monthly Primary Insurance Amount (PIA)
                        <Tooltip
                          tag="i"
                          className="fa fa-question-circle what-ifs-more-info"
                          tooltipchildren={ADV.tooltip_callouts.SocialSecurity.PIA.html}
                          orientation="left"
                        />
                      </>
                    )}
                  </h5>
                </li>
                <li className="what-ifs-form-field what-ifs-form-field-no-box inline">
                  <label htmlFor="SocialSecurityAmountPrincipal">
                    <span className="what-if-title">Amount</span>
                    <AdvInputWrapper hasCurrency className="what-ifs-input-wrapper">
                      <AdvInput
                        id="SocialSecurityAmountPrincipal"
                        type="text"
                        dataType="money"
                        maxLength="14"
                        onBlur={advInputTextChanged(
                          clientId,
                          storeData(goalId, whatIfTemplate.WhatIfType),
                          'WhatIf.SocialSecurityAmountPrincipal',
                        )}
                        value={ADV.util.formatValue(amountPrincipal, 'money')}
                        className="what-ifs-input"
                      />
                    </AdvInputWrapper>
                  </label>
                </li>

                {socialSecurityCurrentlyReceivingBenefitsPrincipal && (
                  <li className="what-ifs-form-field what-ifs-form-field-no-box inline">
                    <label htmlFor={`frequency-${partnerName}`}>
                      <span className="what-if-title">Frequency</span>
                      <SimpleSelect
                        id={`frequency-${partnerName}`}
                        className="what-ifs-simple-select"
                        value={FREQUENCY_OPTIONS.find(
                          ({ value }) => value === socialSecurityIncomeFrequencyPrincipal,
                        )}
                        onValueChange={simpleSelectChangedToNonNull(
                          clientId,
                          storeData(goalId, whatIfTemplate.WhatIfType),
                          'WhatIf.SocialSecurityIncomeFrequencyPrincipal',
                        )}
                        options={FREQUENCY_OPTIONS}
                      />
                    </label>
                  </li>
                )}

                <li className="what-ifs-form-field">
                  <h5 className="what-if-title">
                    {socialSecurityCurrentlyReceivingBenefitsPrincipal
                      ? 'At what age did you begin receiving Social Security Benefits'
                      : 'We change the age we start taking Social Security benefits?'}
                  </h5>
                </li>

                <li className="what-ifs-form-field">
                  <SimpleSelect
                    placeholder="Age"
                    id="PartnerAge"
                    className="what-ifs-simple-select what-ifs-age-select full-width"
                    value={
                      initialPrincipalSelectedAge !== null
                        ? simpleSelectDefaultValue(principalAgeOptions, initialPrincipalSelectedAge)
                        : simpleSelectDefaultValue(principalAgeOptions, principalSelectedAge)
                    }
                    onValueChange={selection => {
                      this.setState(state => ({ ...state, initialPrincipalSelectedAge: null }));

                      return simpleSelectChangedToNonNull(
                        clientId,
                        storeData(goalId, whatIfTemplate.WhatIfType),
                        'WhatIf.SocialSecurityStartAgePrincipal',
                      )(selection);
                    }}
                    options={principalAgeOptions}
                  />
                </li>
              </ul>
            )}

            {/* PARTNER */}
            {hasPartner && !spouseIsCurrentlyReceivingBenefitsUnderCurrentPlan && (
              <ul
                className={`what-ifs-form-fields what-ifs-box${areOptionsLoaded ? ' loaded' : ''}`}
              >
                <li className="what-ifs-form-field">
                  <h5 className="what-if-title">
                    {partnerName}{' '}
                    {socialSecurityCurrentlyReceivingBenefitsPartner ? (
                      'Current Benefit Amount'
                    ) : (
                      <>
                        Change monthly Primary Insurance Amount (PIA)
                        <Tooltip
                          tag="i"
                          className="fa fa-question-circle what-ifs-more-info"
                          tooltipchildren={ADV.tooltip_callouts.SocialSecurity.PIA.html}
                          orientation="left"
                        />
                      </>
                    )}
                  </h5>
                </li>

                <li className="what-ifs-form-field what-ifs-form-field-no-box inline">
                  <label htmlFor="SocialSecurityAmountPrincipal">
                    <span className="what-if-title">Amount</span>
                    <AdvInputWrapper hasCurrency className="what-ifs-input-wrapper">
                      <AdvInput
                        id="SocialSecurityAmountPrincipal"
                        type="text"
                        dataType="money"
                        maxLength="14"
                        onBlur={advInputTextChanged(
                          clientId,
                          storeData(goalId, whatIfTemplate.WhatIfType),
                          'WhatIf.SocialSecurityAmountSpouse',
                        )}
                        value={ADV.util.formatValue(amountSpouse, 'money')}
                        className="what-ifs-input"
                      />
                    </AdvInputWrapper>
                  </label>
                </li>

                {socialSecurityCurrentlyReceivingBenefitsPartner && (
                  <li className="what-ifs-form-field what-ifs-form-field-no-box inline">
                    <label htmlFor={`frequency-${partnerName}`}>
                      <span className="what-if-title">Frequency</span>
                      <SimpleSelect
                        id={`frequency-${partnerName}`}
                        className="what-ifs-simple-select"
                        value={FREQUENCY_OPTIONS.find(
                          ({ value }) => value === socialSecurityIncomeFrequencySpouse,
                        )}
                        onValueChange={simpleSelectChangedToNonNull(
                          clientId,
                          storeData(goalId, whatIfTemplate.WhatIfType),
                          'WhatIf.SocialSecurityIncomeFrequencySpouse',
                        )}
                        options={FREQUENCY_OPTIONS}
                      />
                    </label>
                  </li>
                )}

                <li className="what-ifs-form-field">
                  <h5 className="what-if-title">
                    {socialSecurityCurrentlyReceivingBenefitsPartner
                      ? 'At what age did you begin receiving Social Security Benefits'
                      : 'We change the age we start taking Social Security benefits?'}
                  </h5>
                </li>

                <li className="what-ifs-form-field">
                  <SimpleSelect
                    placeholder="Age"
                    id="PartnerAge"
                    className="what-ifs-simple-select what-ifs-age-select full-width"
                    value={
                      initialSpouseSelectedAge
                        ? simpleSelectDefaultValue(spouseAgeOptions, initialSpouseSelectedAge)
                        : simpleSelectDefaultValue(spouseAgeOptions, spouseSelectedAge)
                    }
                    onValueChange={selection => {
                      this.setState(state => ({
                        ...state,
                        initialSpouseSelectedAge: null,
                      }));

                      return simpleSelectChangedToNonNull(
                        clientId,
                        storeData(goalId, whatIfTemplate.WhatIfType),
                        'WhatIf.SocialSecurityStartAgeSpouse',
                      )(selection);
                    }}
                    options={spouseAgeOptions}
                  />
                </li>
              </ul>
            )}

            {hasPartner && (
              <ul
                className={`what-ifs-form-fields what-ifs-form-fields-no-box${
                  areOptionsLoaded ? ' loaded' : ''
                }`}
              >
                <li className="what-ifs-form-field what-ifs-form-field-no-box">
                  <label
                    htmlFor="UseSpousalBenefit"
                    className="what-ifs-age-label what-ifs-checkbox"
                  >
                    <input
                      type="checkbox"
                      id="UseSpousalBenefit"
                      className="what-ifs-checkbox-toggle"
                      checked={useSpousalBenefit}
                      onChange={inputCheckboxClicked(
                        clientId,
                        storeData(goalId, whatIfTemplate.WhatIfType),
                        'WhatIf.SocialSecurityUseSpousalBenefit',
                      )}
                    />
                    <i className="what-ifs-checkbox-mark" />
                    <span className="what-ifs-checkbox-label-text">
                      We file for spousal benefits
                    </span>
                  </label>
                </li>
                <li className="what-ifs-form-field what-ifs-form-field-no-box">
                  <label
                    htmlFor="FileForSurvivorBenefits"
                    className="what-ifs-age-label what-ifs-checkbox"
                  >
                    <input
                      type="checkbox"
                      id="FileForSurvivorBenefits"
                      className="what-ifs-checkbox-toggle"
                      checked={fileForSurvivorBenefits}
                      onChange={inputCheckboxClicked(
                        clientId,
                        storeData(goalId, whatIfTemplate.WhatIfType),
                        'WhatIf.SocialSecurityFileForSurvivorBenefits',
                      )}
                    />
                    <i className="what-ifs-checkbox-mark" />
                    <span className="what-ifs-checkbox-label-text">
                      We file for survivor benefits
                    </span>
                  </label>
                </li>
              </ul>
            )}
          </AdvForm>
        </div>
        <PlanImpact clientId={clientId} planImpactItems={planImpactItems} />
        <Footer
          applyDisabled={!hasPartner && principalIsCurrentlyReceivingBenefitsUnderCurrentPlan}
          clientId={clientId}
          applyToPlanCallback={applyWhatIf(clientId, fpId, goalId, whatIf, whatIfTemplate)}
        />
      </div>
    );
  }
}
MonthlyBenefitsView.defaultProps = {
  groupTitle: 'Untitled What If Group',
  partnerName: null,
  partnerAge: 0,
  fileForSurvivorBenefits: false,
  useSpousalBenefit: false,
  amountSpouse: 0,
};

MonthlyBenefitsView.propTypes = {
  goalId: createCustomPropType(isUuid).isRequired,
  fpId: createCustomPropType(isUuid).isRequired,
  clientId: createCustomPropType(isNumeric).isRequired,
  groupTitle: PropTypes.string,
  areOptionsLoaded: PropTypes.bool.isRequired,
  whatIf: PropTypes.shape({}).isRequired,
  whatIfTemplate: PropTypes.shape({}).isRequired,
  optimizeSocialSecurity: PropTypes.func.isRequired,
  fetchData: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  fetchPlanImpact: PropTypes.func.isRequired,
  fetchInitialPlanImpact: PropTypes.func.isRequired,
  planImpactItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      current: PropTypes.string.isRequired,
      proposed: PropTypes.string.isRequired,
    }),
  ).isRequired,
  applyWhatIf: PropTypes.func.isRequired,
  storeData: PropTypes.func.isRequired,

  principalName: PropTypes.string.isRequired,
  hasPartner: PropTypes.bool.isRequired,
  principalSelectedAge: PropTypes.oneOf(PropTypes.string, PropTypes.string).isRequired,
  partnerName: PropTypes.string,
  spouseSelectedAge: PropTypes.oneOf(PropTypes.string, PropTypes.string).isRequired,
  fileForSurvivorBenefits: PropTypes.bool,
  useSpousalBenefit: PropTypes.bool,
  amountSpouse: createCustomPropType(isNumeric),
  amountPrincipal: createCustomPropType(isNumeric).isRequired,
  socialSecurityCurrentlyReceivingBenefitsPrincipal: PropTypes.bool.isRequired,
  socialSecurityCurrentlyReceivingBenefitsPartner: PropTypes.bool.isRequired,
  socialSecurityIncomeFrequencyPrincipal: PropTypes.number.isRequired,
  socialSecurityIncomeFrequencySpouse: PropTypes.number.isRequired,
  principalAgeOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.number, label: PropTypes.string }),
  ).isRequired,
  spouseAgeOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.number, label: PropTypes.string }),
  ).isRequired,
  principalIsCurrentlyReceivingBenefitsUnderCurrentPlan: PropTypes.bool.isRequired,
  spouseIsCurrentlyReceivingBenefitsUnderCurrentPlan: PropTypes.bool.isRequired,
  spouseIfFRA: PropTypes.bool.isRequired,
  principalIsFRA: PropTypes.bool.isRequired,
};

export default MonthlyBenefitsView;
